import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import LocaleShell from '@/components/locale/LocaleShell';
import LocaleSection from '@/components/locale/LocaleSection';
import LocaleCards from '@/components/locale/LocaleCards';
import LocaleFAQ from '@/components/locale/LocaleFAQ';
import LocaleCTA from '@/components/locale/LocaleCTA';
import LocaleResources from '@/components/locale/LocaleResources';
import PageNotFound from '@/lib/PageNotFound';
import { CONTENT } from '@/content/locales';
import { LOCALES, SERVICE_KEYS, AE_SERVICE_PATHS, hubPath, servicePath, serviceKeyFromSlug, serviceAlternates, ogAlternatesFor } from '@/lib/locales';
import { UI } from '@/lib/localeUi';
import { SITE_URL, ORG_REF, breadcrumbLd, faqLd } from '@/lib/structuredData';

export default function LocaleService({ cc, slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const key = serviceKeyFromSlug(cc, slug);
  if (!key) return <PageNotFound />;

  const loc = LOCALES[cc];
  const { hub, services } = CONTENT[cc];
  const s = services[key];
  const ui = UI[loc.lang];
  const hub_ = hubPath(cc);
  const path = servicePath(cc, key);
  const crumbs = [
    { label: ui.home, to: '/' },
    { label: loc.label, to: hub_ },
    { label: ui.services, to: `${hub_}#services` },
    { label: s.name, to: path }
  ];

  return (
    <LocaleShell after={<LocaleCTA cta={{ ...hub.cta, title: s.ctaTitle }} contactTo={`${hub_}#contact`} />}>
      <SEO
        fullTitle={s.title} description={s.meta} path={path}
        lang={loc.hreflang} ogLocale={loc.og} ogAlternates={ogAlternatesFor(loc.og)} alternates={serviceAlternates(key)}
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            { '@type': 'Service', name: s.h1, serviceType: s.serviceType, description: s.meta, url: `${SITE_URL}${path}`, inLanguage: loc.hreflang, provider: ORG_REF, areaServed: { '@type': 'Country', name: loc.country } },
            breadcrumbLd(crumbs),
            faqLd(s.faqs)
          ]
        }}
      />
      <Breadcrumbs items={crumbs} />
      <span className="text-xs font-inter font-medium tracking-[0.3em] uppercase text-primary mb-4 block">{loc.label}</span>
      <h1 className="font-inter font-black text-5xl md:text-7xl tracking-[-0.03em] text-foreground mb-6 leading-[1.05]">{s.h1}</h1>
      <p className="text-lg text-muted-foreground font-inter leading-relaxed mb-16">{s.intro}</p>

      <LocaleSection title={ui.included} bullets={s.included} />
      <LocaleSection title={ui.how}>
        <LocaleCards items={s.steps} numbered />
      </LocaleSection>
      <LocaleSection title={ui.compliance(loc.inCountry)} paras={s.compliance} />
      <LocaleSection title={ui.projects(loc.inCountry)}>
        <LocaleCards items={s.projects} />
      </LocaleSection>
      <LocaleFAQ title={ui.faq} faqs={s.faqs} />
      <LocaleResources title={ui.resources} note={ui.resourceNote} items={s.related} />

      <section className="mb-8 space-y-4">
        <Link to={AE_SERVICE_PATHS[key]} className="block text-sm text-primary font-inter hover:underline">{ui.globalLink} →</Link>
        <p className="text-xs font-inter font-medium tracking-[0.2em] uppercase text-foreground">{ui.otherServices}</p>
        <div className="flex flex-wrap gap-4">
          {SERVICE_KEYS.filter((k) => k !== key).map((k) => (
            <Link key={k} to={servicePath(cc, k)} className="text-sm text-muted-foreground font-inter hover:text-primary transition-colors">{services[k].name}</Link>
          ))}
        </div>
      </section>
    </LocaleShell>
  );
}